import React, { forwardRef, useImperativeHandle, useRef } from "react";
import ReactDOM from "react-dom/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Paper,
  PaperProps,
  Typography,
} from "@mui/material";
import { showDialog } from "@widgets/CommonDialog";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";

interface IssueModalProps {
  title: string;
  onClose: (result: any) => void;
  children?: React.ReactNode;
  code?: string;
  width?: string;
  height?: string;
  okayBtn?: boolean;
}

function PaperComponent(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

const IssueModal = forwardRef(
  (
    {
      code,
      title,
      onClose,
      children,
      width,
      height,
      okayBtn = true,
    }: IssueModalProps,
    modalRef: any
  ) => {
    const internalRef = useRef<any>(null);
    const dialogContentRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(modalRef, () => ({
      handleConfirm: (flag: boolean) => handleConfirm(flag),
      getPopupParent: () => dialogContentRef.current,
    }));

    const handleConfirm = async (okayFlag: boolean) => {
      if (!okayFlag) {
        onClose(undefined);
        return;
      }

      if (
        code === "POPUP_RECIPIENT" ||
        code === "POPUP_REFERENCE" ||
        code === "POPUP_MENU"
      ) {
        const result = await internalRef.current?.getSelectedRow();
        if (result && result.length > 0) {
          onClose(result);
          return;
        } else if (result && result.length === 0) {
          await showDialog("alter", "선택된 내용이 없습니다.");
          return;
        }
        return;
      } else {
        const result = await internalRef.current?.onSave?.();
        if (typeof result === "object") {
          if (result.status) {
            onClose(result.data);
            return result.data;
          }
        } else {
          if (result) {
            onClose(true);
          } else return;
        }
      }
    };

    const handleClose = () => {
      onClose("close");
    };

    const renderBody = () => {
      if (children && React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement, {
          modalRef: modalRef,
          ref: internalRef,
        });
      }
      return null;
    };

    return (
      <Dialog
        disableEnforceFocus
        open={true}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        onClose={(e: any, reason) => {
          if (reason === "escapeKeyDown") {
            e.preventDefault();
            handleClose();
          }
        }}
        maxWidth={false}
        PaperComponent={PaperComponent}
        slotProps={{
          root: {
            sx: {
              position: "absolute",
            },
          },
          paper: {
            sx: {
              width: width ?? "50%",
              height: height ?? "90%",
              padding: "15px",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              boxShadow:
                "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
              position: "absolute",
              margin: 0,
            },
          },
        }}
      >
        <DialogTitle
          style={{ cursor: "move" }}
          className="draggable-modal"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: 0,
          }}
        >
          <Typography id="modal-title" variant="h6">
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ marginLeft: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          ref={dialogContentRef}
          id="modal-description"
          sx={{
            mt: 2,
            maxWidth: "100%",
            height: "100%",
            overflow: "auto",
            wordWrap: "break-word",
            overflowWrap: "break-word",
            px: 1.5,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            paddingBottom: 0,
          }}
        >
          {renderBody()}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center" }}>
          {okayBtn && (
            <Button
              onClick={() => handleConfirm(true)}
              variant="contained"
              sx={{ width: 80 }}
            >
              확인
            </Button>
          )}
          <Button
            onClick={() => handleConfirm(false)}
            variant="outlined"
            sx={{ width: 80 }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export const showIssueModal = (options: {
  title: string;
  children: React.ReactNode;
  code?: string;
  width?: string;
  height?: string;
  okayBtn?: boolean;
}): Promise<any> => {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  const modalRef = React.createRef<any>(); // modalRef 생성

  return new Promise((resolve) => {
    const handleClose = (result: any) => {
      root.unmount();
      document.body.removeChild(container);
      resolve(result);
    };

    root.render(
      <IssueModal
        ref={modalRef}
        code={options.code}
        title={options.title}
        onClose={handleClose}
        children={options.children}
        width={options.width}
        height={options.height}
        okayBtn={options.okayBtn}
      />
    );
  });
};
